import { FC, memo, useState, useEffect } from 'react'
import { useRouter } from 'next/router'
import { useTranslation } from 'next-i18next'

import SVGImage from '~/components/Image/SVGImage'
import { Link } from '~/components/Link'
import { TopicOverview } from '~/types/theory'
import { getIconName, getTopicClassName } from '~/encyclopedia/utils/encyclopedia.utils'

import { SubTopics } from './SubTopics'
interface HorizontalMenuItemProps {
  topic: TopicOverview
}

const HorizontalMenuItemComponent: FC<HorizontalMenuItemProps> = ({ topic }) => {
  const [showTopicSubMenu, setShowTopicSubMenu] = useState(false)
  const [activeClass, setActiveClass] = useState({ path: '', className: '' })
  const isDisabled = topic.children?.length === 0
  const route = useRouter()

  const { t } = useTranslation('encyclopedia')

  useEffect(() => {
    setActiveClass({ path: '', className: '' })
    setShowTopicSubMenu(false)
  }, [route.asPath])

  const handleOnMouseLeave = (): void => {
    setActiveClass({ path: '', className: '' })
    setShowTopicSubMenu(false)
  }

  const handleOnMouseEnter = (mainTopic: TopicOverview): void => {
    if (!isDisabled) {
      setShowTopicSubMenu(true)
      setActiveClass({
        path: mainTopic.path,
        className: getTopicClassName(mainTopic),
      })
    }
  }

  return (
    <div className="h-full py-3" onMouseLeave={handleOnMouseLeave}>
      <Link
        className={
          'h-10 px-2 2xl:px-3 flex items-center rounded text-xxs 2xl:text-xs text-gray-link tracking-normal ' +
          `${isDisabled ? 'cursor-not-allowed' : activeClass.className}`
        }
        onMouseEnter={() => handleOnMouseEnter(topic)}
        to={isDisabled ? '#' : topic.path}
      >
        <SVGImage
          alt={t(getIconName(topic.path).alt)}
          height={20}
          imageClassName="w-5 mr-3"
          srcKey={getIconName(topic.path)?.src}
          width={20}
        />
        <span>{topic.h1}</span>
      </Link>

      {showTopicSubMenu && topic?.children && (
        <div className="absolute min-w-max inset-0 transform translate-y-full z-50">
          <div className="w-full mt-1 py-4 pr-4 flex rounded bg-white shadow-experimental">
            <SubTopics subTopics={topic.children} />
          </div>
        </div>
      )}
    </div>
  )
}

export const HorizontalMenuItem = memo(HorizontalMenuItemComponent)
